package net.sergeych.bintools

import kotlinx.serialization.Serializable
import net.sergeych.mp_tools.encodeToBase64Url
import kotlin.math.min
import kotlin.random.Random

/**
 * Bytes sequence with comparison, concatenation, and string representation,
 * could be used as hash keys for pure binary values, etc.
 */
@Suppress("unused")
@Serializable
class ByteChunk(val data: UByteArray): Comparable<ByteChunk> {

    val size: Int get() = data.size

    /**
     * Per-byte comparison also of different length. From two chunks
     * of different size but equal beginning, the shorter is considered
     * the smaller.
     */
    override fun compareTo(other: ByteChunk): Int {
        val limit = min(size, other.size)
        for( i in 0 ..< limit) {
            val own = data[i]
            val their = other.data[i]
            if( own < their) return -1
            else if( own > their) return 1
        }
        if( size < other.size ) return -1
        if( size > other.size ) return 1
        return 0
    }

    /**
     * Equal chunks means content equality.
     */
    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is ByteChunk) return false

        return data contentEquals other.data
    }

    /**
     * Content-based hash code
     */
    override fun hashCode(): Int {
        return data.contentHashCode()
    }

    /**
     * hex representation of data
     */
    override fun toString(): String = base64

    /**
     * Hex encoded data
     */
    val hex by lazy { data.encodeToHex() }

    /**
     * human-readable dump
     */
    val dump by lazy { data.toDump() }

    /**
     * Lazy encode to base64 with url alphabet, without trailing fill '=' characters.
     */
    val base64 by lazy { data.asByteArray().encodeToBase64Url() }

    /**
     * Lazy (cached) view of [data] as ByteArray
     */
    val asByteArray: ByteArray by lazy { data.asByteArray() }

    /**
     * Concatenate two chunks and return new one
     */
    operator fun plus(other: ByteChunk): ByteChunk = ByteChunk(data + other.data)

    companion object {
        fun fromHex(hex: String): ByteChunk = ByteChunk(hex.decodeHex().asUByteArray())
        fun random(size: Int=16): ByteChunk = Random.nextBytes(size).asChunk()
    }
}

/**
 * Create the representation of this array as ByteChunk; it does not copy the data.
 */
fun ByteArray.asChunk() = ByteChunk(this.asUByteArray())

/**
 * Create the representation of this array as ByteChunk; it does not copy the data.
 */
@Suppress("unused")
fun UByteArray.asChunk() = ByteChunk(this)